import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import {
  Mic,
  MicOff,
  Copy,
  Check,
  Users,
  Globe,
  MessageSquare,
  Loader2,
  ArrowRight,
  Square,
  Share2,
  FileText,
  Sparkles,
  QrCode,
  X,
  Link as LinkIcon,
} from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + '/api';

// All supported languages
const LANGUAGES = {
  pl: { name: 'Polski', flag: '🇵🇱', code: 'pl-PL' },
  de: { name: 'Deutsch', flag: '🇩🇪', code: 'de-DE' },
  en: { name: 'English', flag: '🇬🇧', code: 'en-US' },
  es: { name: 'Español', flag: '🇪🇸', code: 'es-ES' },
  fr: { name: 'Français', flag: '🇫🇷', code: 'fr-FR' },
  it: { name: 'Italiano', flag: '🇮🇹', code: 'it-IT' },
  uk: { name: 'Українська', flag: '🇺🇦', code: 'uk-UA' },
  ru: { name: 'Русский', flag: '🇷🇺', code: 'ru-RU' },
  zh: { name: '中文', flag: '🇨🇳', code: 'zh-CN' },
  ja: { name: '日本語', flag: '🇯🇵', code: 'ja-JP' },
  ko: { name: '한국어', flag: '🇰🇷', code: 'ko-KR' },
  ar: { name: 'العربية', flag: '🇸🇦', code: 'ar-SA' },
  pt: { name: 'Português', flag: '🇵🇹', code: 'pt-PT' },
  nl: { name: 'Nederlands', flag: '🇳🇱', code: 'nl-NL' },
  tr: { name: 'Türkçe', flag: '🇹🇷', code: 'tr-TR' },
  cs: { name: 'Čeština', flag: '🇨🇿', code: 'cs-CZ' },
};

const LiveTranslation = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, user, token } = useApp();
  
  // Session state
  const [session, setSession] = useState(null);
  const [myLanguage, setMyLanguage] = useState(searchParams.get('lang') || 'pl');
  const [partnerLanguage, setPartnerLanguage] = useState('de');
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Recording state
  const [isListening, setIsListening] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);

  // Refs
  const recognitionRef = useRef(null);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const pendingTextRef = useRef('');
  const silenceTimerRef = useRef(null);
  const isListeningRef = useRef(false);
  const isMountedRef = useRef(true);
  const reconnectTimerRef = useRef(null);
  const SILENCE_DELAY = 2000; // Wait 2 seconds of silence before translating
  
  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Load session
  useEffect(() => {
    isMountedRef.current = true;
    if (sessionId) {
      loadSession().then(() => connectWebSocket());
    }
    return () => {
      isMountedRef.current = false;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [sessionId]);
  
  const loadSession = async () => {
    try {
      const authToken = token || localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/live/session/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSession(data);
        setMessages(data.messages || []);
        
        // Set languages based on URL param or session
        const urlLang = searchParams.get('lang');
        if (urlLang && (urlLang === data.language1 || urlLang === data.language2)) {
          setMyLanguage(urlLang);
          setPartnerLanguage(urlLang === data.language1 ? data.language2 : data.language1);
        } else {
          setMyLanguage(data.language1);
          setPartnerLanguage(data.language2);
        }
      }
    } catch (error) {
      toast.error('Failed to load session');
    }
  };
  
  const connectWebSocket = () => {
    const token = localStorage.getItem('token');
    const wsUrl = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/^https?/, (m) => m === 'https' ? 'wss' : 'ws') + `/ws/live/${sessionId}?token=${token}`;
    wsRef.current = new WebSocket(wsUrl);
    
    wsRef.current.onopen = () => {
      setIsConnected(true);
      toast.success('Connected to session');
    };
    
    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'message') {
        setMessages(prev => [...prev, data.message]);
      }
    };
    
    wsRef.current.onclose = () => {
      setIsConnected(false);
      // Auto-reconnect after 3 seconds if still mounted
      if (isMountedRef.current && sessionId) {
        reconnectTimerRef.current = setTimeout(() => {
          if (isMountedRef.current && sessionId) connectWebSocket();
        }, 3000);
      }
    };
    
    wsRef.current.onerror = () => {
      toast.error('Connection error');
    };
  };
  
  // Speech Recognition setup
  const startListening = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = LANGUAGES[myLanguage].code;
    
    recognitionRef.current.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      
      // Show current text (interim or final)
      const currentText = pendingTextRef.current + (interimTranscript || finalTranscript);
      setCurrentTranscript(currentText);
      
      if (finalTranscript) {
        // Accumulate final transcript
        pendingTextRef.current += finalTranscript + ' ';
        
        // Reset silence timer - wait for pause before translating
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
        }
        
        silenceTimerRef.current = setTimeout(() => {
          const textToTranslate = pendingTextRef.current.trim();
          if (textToTranslate) {
            handleFinalTranscript(textToTranslate);
            pendingTextRef.current = '';
          }
        }, SILENCE_DELAY);
      }
    };
    
    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error !== 'no-speech') {
        toast.error(`Speech recognition error: ${event.error}`);
      }
    };
    
    recognitionRef.current.onend = () => {
      if (isListeningRef.current) {
        recognitionRef.current.start();
      }
    };

    recognitionRef.current.start();
    setIsListening(true);
    isListeningRef.current = true;
  };
  
  const stopListening = () => {
    // Translate any pending text before stopping
    if (pendingTextRef.current.trim()) {
      handleFinalTranscript(pendingTextRef.current.trim());
      pendingTextRef.current = '';
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
    }
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
    isListeningRef.current = false;
    setCurrentTranscript('');
  };
  
  const handleFinalTranscript = async (text) => {
    if (!text.trim()) return;
    
    setIsTranslating(true);
    setCurrentTranscript('');
    
    try {
      // Translate text
      const authToken = token || localStorage.getItem('token');
      const translateRes = await fetch(`${API_BASE}/live/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({
          text: text,
          source_lang: myLanguage,
          target_lang: partnerLanguage
        })
      });
      
      if (!translateRes.ok) throw new Error('Translation failed');
      const { translated_text } = await translateRes.json();
      
      const newMessage = {
        id: Date.now().toString(),
        speaker: user?.name || 'User',
        original_text: text,
        translated_text: translated_text,
        source_lang: myLanguage,
        target_lang: partnerLanguage,
        timestamp: new Date().toISOString(),
        isMine: true
      };
      
      // Add to local messages
      setMessages(prev => [...prev, newMessage]);
      
      // Send via WebSocket
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'message', message: { ...newMessage, isMine: false } }));
      }
      
      // Save to backend
      await fetch(`${API_BASE}/live/session/${sessionId}/message`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          speaker: newMessage.speaker,
          original_text: text,
          translated_text: translated_text,
          source_lang: myLanguage,
          target_lang: partnerLanguage
        })
      });
      
    } catch (error) {
      toast.error('Translation failed');
    } finally {
      setIsTranslating(false);
    }
  };
  
  const copySessionLink = () => {
    const link = `${window.location.origin}/live/${sessionId}?lang=${partnerLanguage}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };
  
  const getShareLink = () => {
    return `${window.location.origin}/live/${sessionId}?lang=${partnerLanguage}`;
  };
  
  const shareSession = () => {
    setShowShareModal(true);
  };
  
  const endSession = async () => {
    stopListening();
    try {
      const res = await fetch(`${API_BASE}/live/session/${sessionId}/end`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.analysis) {
        toast.success('Session ended and analyzed');
        navigate(`/live/${sessionId}/summary`);
      }
    } catch (error) {
      toast.error('Failed to end session');
    }
  };
  
  const swapLanguages = () => {
    const temp = myLanguage;
    setMyLanguage(partnerLanguage);
    setPartnerLanguage(temp);
    if (isListening) {
      stopListening();
      setTimeout(startListening, 100);
    }
  };
  
  if (!sessionId) {
    return <CreateSession />;
  }
  
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Globe className="w-6 h-6 text-violet-600" />
              Live Translation
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="font-mono">{sessionId}</Badge>
              <Badge variant={isConnected ? 'default' : 'secondary'}>
                {isConnected ? 'Connected' : 'Connecting...'}
              </Badge>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={shareSession} className="rounded-full" data-testid="share-btn">
              <QrCode className="w-4 h-4 mr-2" />
              Share
            </Button>
            <Button 
              size="sm" 
              onClick={endSession} 
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" 
              data-testid="end-session-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              End & Analyze
            </Button>
          </div>
        </div>
        
        {/* Share Modal with QR Code */}
        {showShareModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowShareModal(false)}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">Share Session</h3>
                <button onClick={() => setShowShareModal(false)} className="p-1 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* QR Code */}
              <div className="flex justify-center mb-6 p-4 bg-white rounded-xl">
                <QRCodeSVG 
                  value={getShareLink()} 
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>
              
              {/* Session Code */}
              <div className="text-center mb-4">
                <p className="text-sm text-muted-foreground mb-1">Session Code</p>
                <p className="text-3xl font-mono font-bold tracking-wider">{sessionId}</p>
              </div>
              
              {/* Link */}
              <div className="flex gap-2 mb-4">
                <Input 
                  value={getShareLink()} 
                  readOnly 
                  className="text-xs font-mono"
                />
                <Button onClick={copySessionLink} size="sm" className="shrink-0">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Native Share (mobile) */}
              {navigator.share && (
                <Button 
                  onClick={() => {
                    navigator.share({
                      title: 'Join Live Translation',
                      text: `Join my translation session: ${sessionId}`,
                      url: getShareLink()
                    });
                  }}
                  className="w-full rounded-full bg-violet-600 hover:bg-violet-700"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share via...
                </Button>
              )}
            </div>
          </div>
        )}
        
        {/* Language selector */}
        <Card className="mb-6 rounded-2xl border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-950/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <div className="text-3xl mb-1">{LANGUAGES[myLanguage].flag}</div>
                <p className="text-sm font-medium">{LANGUAGES[myLanguage].name}</p>
                <p className="text-xs text-muted-foreground">You speak</p>
              </div>
              
              <Button variant="ghost" size="icon" onClick={swapLanguages} className="rounded-full" data-testid="swap-languages-btn">
                <ArrowRight className="w-5 h-5" />
              </Button>
              
              <div className="text-center">
                <div className="text-3xl mb-1">{LANGUAGES[partnerLanguage].flag}</div>
                <p className="text-sm font-medium">{LANGUAGES[partnerLanguage].name}</p>
                <p className="text-xs text-muted-foreground">Partner speaks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Main conversation area */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* My language side */}
          <Card className="rounded-2xl overflow-hidden" data-testid="my-language-panel">
            <CardHeader className="bg-slate-100 dark:bg-slate-800 py-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="text-2xl">{LANGUAGES[myLanguage].flag}</span>
                {LANGUAGES[myLanguage].name}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[350px] p-4">
                <div className="space-y-3">
                  {messages.map((msg, i) => {
                    const isMyMessage = msg.source_lang === myLanguage;
                    return (
                      <div key={msg.id || i} className={`p-3 rounded-xl ${isMyMessage ? 'bg-violet-100 dark:bg-violet-900/30 ml-8' : 'bg-blue-100 dark:bg-blue-900/30 mr-8'}`}>
                        <p className="text-sm font-medium text-muted-foreground mb-1">
                          {msg.speaker}{!isMyMessage ? ' (translated)' : ''}
                        </p>
                        <p className="text-foreground">{isMyMessage ? msg.original_text : msg.translated_text}</p>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>
              
              {/* Current transcript preview */}
              {(currentTranscript || isTranslating) && (
                <div className="border-t border-slate-200 dark:border-slate-700 p-3 bg-slate-50 dark:bg-slate-900">
                  {isTranslating ? (
                    <div className="flex items-center gap-2 text-violet-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Translating...</span>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">{currentTranscript}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Partner language side */}
          <Card className="rounded-2xl overflow-hidden" data-testid="partner-language-panel">
            <CardHeader className="bg-slate-100 dark:bg-slate-800 py-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="text-2xl">{LANGUAGES[partnerLanguage].flag}</span>
                {LANGUAGES[partnerLanguage].name}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[350px] p-4">
                <div className="space-y-3">
                  {messages.map((msg, i) => {
                    const isPartnerMessage = msg.source_lang === partnerLanguage;
                    return (
                      <div key={msg.id || i} className={`p-3 rounded-xl ${isPartnerMessage ? 'bg-violet-100 dark:bg-violet-900/30 ml-8' : 'bg-green-100 dark:bg-green-900/30 ml-8'}`}>
                        <p className="text-sm font-medium text-muted-foreground mb-1">
                          {msg.speaker}{!isPartnerMessage ? ' (translated)' : ''}
                        </p>
                        <p className="text-foreground">{isPartnerMessage ? msg.original_text : msg.translated_text}</p>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
        
        {/* Recording controls */}
        <div className="flex justify-center mt-8">
          <Button
            size="lg"
            onClick={isListening ? stopListening : startListening}
            className={`h-20 w-20 rounded-full shadow-xl transition-all ${
              isListening 
                ? 'bg-red-500 hover:bg-red-600 recording-pulse' 
                : 'bg-violet-600 hover:bg-violet-700'
            }`}
            data-testid="record-button"
          >
            {isListening ? <MicOff className="w-8 h-8" /> : <Mic className="w-8 h-8" />}
          </Button>
        </div>
        <p className="text-center text-sm text-muted-foreground mt-3">
          {isListening ? `Listening in ${LANGUAGES[myLanguage].name}... Speak now!` : 'Tap to start speaking'}
        </p>
      </div>
    </div>
  );
};

// Create Session Component
const CreateSession = () => {
  const navigate = useNavigate();
  const { token } = useApp();
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [language1, setLanguage1] = useState('pl');
  const [language2, setLanguage2] = useState('de');
  const [person1Name, setPerson1Name] = useState('');
  const [person2Name, setPerson2Name] = useState('');
  const [joinCode, setJoinCode] = useState('');
  
  const createSession = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE}/live/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          title, 
          description, 
          language1, 
          language2,
          person1_name: person1Name || 'Person 1',
          person2_name: person2Name || 'Person 2'
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Session created!');
        navigate(`/live/${data.session_id}?lang=${language1}`);
      }
    } catch (error) {
      toast.error('Failed to create session');
    } finally {
      setCreating(false);
    }
  };
  
  const joinSession = () => {
    if (joinCode.trim()) {
      navigate(`/live/${joinCode.trim().toUpperCase()}`);
    }
  };
  
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-violet-50 to-slate-100 dark:from-slate-950 dark:to-violet-950/20">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-2xl bg-violet-600 flex items-center justify-center mx-auto mb-6">
            <Globe className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight mb-4">Live Translation</h1>
          <p className="text-lg text-muted-foreground">
            Real-time conversation translation between two languages
          </p>
        </div>
        
        {/* Create Session */}
        <Card className="rounded-2xl mb-6" data-testid="create-session-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Start New Conversation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Title and Description */}
            <div>
              <label className="text-sm font-medium mb-2 block">Session Name (optional)</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Trade Fair Meeting, Client Call..."
                className="h-11 rounded-lg"
                data-testid="session-title-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Description (optional)</label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief note about this conversation..."
                className="h-11 rounded-lg"
                data-testid="session-description-input"
              />
            </div>
            
            {/* Participants */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">👤 Participant 1 Name</label>
                <Input
                  value={person1Name}
                  onChange={(e) => setPerson1Name(e.target.value)}
                  placeholder="e.g., Anna, Customer..."
                  className="h-11 rounded-lg"
                  data-testid="person1-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">👤 Participant 2 Name</label>
                <Input
                  value={person2Name}
                  onChange={(e) => setPerson2Name(e.target.value)}
                  placeholder="e.g., Jan, Supplier..."
                  className="h-11 rounded-lg"
                  data-testid="person2-name-input"
                />
              </div>
            </div>
            
            {/* Languages */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Your Language</label>
                <select
                  value={language1}
                  onChange={(e) => setLanguage1(e.target.value)}
                  className="w-full h-12 rounded-lg border border-input bg-background px-3"
                  data-testid="language1-select"
                >
                  {Object.entries(LANGUAGES).map(([code, lang]) => (
                    <option key={code} value={code}>{lang.flag} {lang.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Partner's Language</label>
                <select
                  value={language2}
                  onChange={(e) => setLanguage2(e.target.value)}
                  className="w-full h-12 rounded-lg border border-input bg-background px-3"
                  data-testid="language2-select"
                >
                  {Object.entries(LANGUAGES).map(([code, lang]) => (
                    <option key={code} value={code}>{lang.flag} {lang.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <Button
              onClick={createSession}
              disabled={creating || language1 === language2}
              className="w-full h-12 rounded-full bg-violet-600 hover:bg-violet-700"
              data-testid="create-session-btn"
            >
              {creating ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <MessageSquare className="w-5 h-5 mr-2" />}
              Start Conversation (Two Devices)
            </Button>
            
            {/* Face-to-Face Mode Button */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200 dark:border-slate-700"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">or</span>
              </div>
            </div>
            
            <Button
              onClick={() => navigate('/live/face-to-face')}
              variant="outline"
              className="w-full h-12 rounded-full border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/20"
              data-testid="face-to-face-btn"
            >
              <Users className="w-5 h-5 mr-2" />
              Face-to-Face Mode (One Screen)
            </Button>
          </CardContent>
        </Card>
        
        {/* Join Session */}
        <Card className="rounded-2xl" data-testid="join-session-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Join Existing Session
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="Enter session code..."
                className="h-12 rounded-lg font-mono text-center text-lg"
                maxLength={8}
                data-testid="join-code-input"
              />
              <Button
                onClick={joinSession}
                disabled={!joinCode.trim()}
                className="h-12 px-6 rounded-full"
                data-testid="join-session-btn"
              >
                Join
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Session History */}
        <SessionHistory token={token} />
      </div>
    </div>
  );
};

// Session History Component
const SessionHistory = ({ token }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  useEffect(() => {
    loadSessions();
  }, []);
  
  const loadSessions = async () => {
    try {
      const res = await fetch(`${API_BASE}/live/sessions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSessions(data);
      }
    } catch (e) {
      console.error('Failed to load sessions:', e);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) return null;
  if (sessions.length === 0) return null;
  
  return (
    <Card className="rounded-2xl mt-6" data-testid="session-history-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Previous Sessions
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {sessions.slice(0, 5).map((session) => (
            <div
              key={session.id}
              onClick={() => navigate(`/live/${session.id}/summary`)}
              className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer transition-colors"
              data-testid={`session-${session.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium truncate">{session.title || `Session ${session.id}`}</span>
                  <Badge variant="outline" className="font-mono text-xs shrink-0">{session.id}</Badge>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{LANGUAGES[session.language1]?.flag}</span>
                  <span>↔</span>
                  <span>{LANGUAGES[session.language2]?.flag}</span>
                  {session.description && (
                    <span className="truncate">• {session.description}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground ml-2">
                <MessageSquare className="w-4 h-4" />
                <span>{session.messages?.length || 0}</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default LiveTranslation;
